import {
  TLBaseShape,
  RecordProps,
  T,
  BoundsSnapGeometry,
  Rectangle2d,
  HTMLContainer,
  BaseBoxShapeUtil,
  Vec,
} from "@tldraw/tldraw";
import React from "react";

type CustomMeasureUtilType = TLBaseShape<
  "customMeasure",
  {
    w: number;
    h: number;
    fromLeft?: {
      distance: number;
      fromX: number;
      toX: number;
      y: number;
    } | null;
    fromRight?: {
      distance: number;
      fromX: number;
      toX: number;
      y: number;
    } | null;
    fromTop?: {
      distance: number;
      fromY: number;
      toY: number;
      x: number;
    } | null;
    fromBottom?: {
      distance: number;
      fromY: number;
      toY: number;
      x: number;
    } | null;
    boundsA?: {
      x: number;
      y: number;
      w: number;
      h: number;
    } | null;
    boundsB?: {
      x: number;
      y: number;
      w: number;
      h: number;
    } | null;
  }
>;

export class CustomMeasureUtil extends BaseBoxShapeUtil<CustomMeasureUtilType> {
  static override type = "customMeasure" as const;
  static override props: RecordProps<CustomMeasureUtilType> = {
    w: T.number,
    h: T.number,
    fromLeft: T.nullable(
      T.object({
        distance: T.number,
        fromX: T.number,
        toX: T.number,
        y: T.number,
      })
    ),
    fromRight: T.nullable(
      T.object({
        distance: T.number,
        fromX: T.number,
        toX: T.number,
        y: T.number,
      })
    ),
    fromTop: T.nullable(
      T.object({
        distance: T.number,
        fromY: T.number,
        toY: T.number,
        x: T.number,
      })
    ),
    fromBottom: T.nullable(
      T.object({
        distance: T.number,
        fromY: T.number,
        toY: T.number,
        x: T.number,
      })
    ),
    boundsA: T.nullable(
      T.object({
        x: T.number,
        y: T.number,
        w: T.number,
        h: T.number,
      })
    ),
    boundsB: T.nullable(
      T.object({
        x: T.number,
        y: T.number,
        w: T.number,
        h: T.number,
      })
    ),
  };

  getDefaultProps(): CustomMeasureUtilType["props"] {
    return {
      w: 100,
      h: 100,
      fromLeft: null,
      fromRight: null,
      fromTop: null,
      fromBottom: null,
      boundsA: null,
      boundsB: null,
    };
  }

  getBoundsSnapGeometry(shape: CustomMeasureUtilType): BoundsSnapGeometry {
    return new Rectangle2d({
      width: shape.props.w,
      height: shape.props.h,
      isFilled: false,
      x: 0,
      y: 0,
    });
  }

  component(shape: CustomMeasureUtilType) {
    const { fromLeft, fromRight, fromTop, fromBottom, boundsA, boundsB, w, h } = shape.props;

    // If no measurements, don't render anything
    if (!fromLeft && !fromRight && !fromTop && !fromBottom) {
      return (
        <HTMLContainer
          style={{
            pointerEvents: "none",
          }}
        >
          <div />
        </HTMLContainer>
      );
    }

    return (
      <HTMLContainer
        style={{
          pointerEvents: "none",
        }}
      >
        <svg viewBox={`0 0 ${w} ${h}`} style={{ overflow: "visible", pointerEvents: "none" }}>
          {/* Measurement from left edge */}
          {fromLeft && boundsA && boundsB && (
            <MeasurementLine
              from={{ x: fromLeft.fromX, y: fromLeft.y }}
              to={{ x: fromLeft.toX, y: fromLeft.y }}
              distance={fromLeft.distance}
              isHorizontal={true}
              boundsA={boundsA}
              boundsB={boundsB}
            />
          )}

          {/* Measurement from right edge */}
          {fromRight && boundsA && boundsB && (
            <MeasurementLine
              from={{ x: fromRight.fromX, y: fromRight.y }}
              to={{ x: fromRight.toX, y: fromRight.y }}
              distance={fromRight.distance}
              isHorizontal={true}
              boundsA={boundsA}
              boundsB={boundsB}
            />
          )}

          {/* Measurement from top edge */}
          {fromTop && boundsA && boundsB && (
            <MeasurementLine
              from={{ x: fromTop.x, y: fromTop.fromY }}
              to={{ x: fromTop.x, y: fromTop.toY }}
              distance={fromTop.distance}
              isHorizontal={false}
              boundsA={boundsA}
              boundsB={boundsB}
            />
          )}

          {/* Measurement from bottom edge */}
          {fromBottom && boundsA && boundsB && (
            <MeasurementLine
              from={{ x: fromBottom.x, y: fromBottom.fromY }}
              to={{ x: fromBottom.x, y: fromBottom.toY }}
              distance={fromBottom.distance}
              isHorizontal={false}
              boundsA={boundsA}
              boundsB={boundsB}
            />
          )}
        </svg>
      </HTMLContainer>
    );
  }

  indicator(shape: CustomMeasureUtilType) {
    return null; // No indicator needed for measurement lines
  }
}

/**
 * Reusable component for rendering a measurement line with label (Figma-style)
 * The label size adapts dynamically based on the distance value
 * Includes extension lines at the END point (hovering shape) to show alignment
 */
const MeasurementLine = ({
  from,
  to,
  distance,
  isHorizontal,
  boundsA,
  boundsB,
}: {
  from: { x: number; y: number };
  to: { x: number; y: number };
  distance: number;
  isHorizontal: boolean;
  boundsA: { x: number; y: number; w: number; h: number };
  boundsB: { x: number; y: number; w: number; h: number };
}) => {
  const midX = (from.x + to.x) / 2;
  const midY = (from.y + to.y) / 2;

  // Calculate line length
  const lineLength = Math.sqrt(Math.pow(to.x - from.x, 2) + Math.pow(to.y - from.y, 2));

  // Dynamic sizing based on distance - more granular levels
  // Extra small for very tiny distances
  const isExtraSmall = distance < 15;
  const isVerySmall = distance < 30;
  const isSmall = distance < 60;
  const isMedium = distance < 120;

  // More aggressive size reduction
  const fontSize = isExtraSmall ? 8 : isVerySmall ? 9 : isSmall ? 10 : isMedium ? 11 : 12;
  const textPadding = isExtraSmall ? 3 : isVerySmall ? 4 : isSmall ? 5 : isMedium ? 7 : 8;
  const textHeight = isExtraSmall ? 12 : isVerySmall ? 14 : isSmall ? 16 : isMedium ? 18 : 20;
  const strokeWidth = isExtraSmall ? 0.8 : isVerySmall ? 1 : isSmall ? 1.2 : 1.5;
  const borderRadius = isExtraSmall ? 1.5 : isVerySmall ? 2 : isSmall ? 2.5 : 3;

  const charWidth = isExtraSmall ? 4.5 : isVerySmall ? 5 : isSmall ? 5.5 : isMedium ? 6 : 6.5;
  const textWidth = distance.toString().length * charWidth + textPadding;

  const safeLineLength = lineLength === 0 ? 1 : lineLength;
  const gap = textWidth;
  const dx = ((to.x - from.x) / safeLineLength) * (gap / 2);
  const dy = ((to.y - from.y) / safeLineLength) * (gap / 2);

  const shouldBreakLine = lineLength > textWidth + 10;

  // Calculate extension lines at the END point (shape B)
  // These lines extend from the measurement point to the edges of shape B
  let extensionLines: Array<{ x1: number; y1: number; x2: number; y2: number }> = [];

  if (isHorizontal) {
    // For horizontal measurements, extend vertically from the TO point
    // to the top and bottom edges of shape B
    extensionLines = [
      // Extension to top edge of shape B
      {
        x1: to.x,
        y1: to.y,
        x2: to.x,
        y2: boundsB.y,
      },
      // Extension to bottom edge of shape B
      {
        x1: to.x,
        y1: to.y,
        x2: to.x,
        y2: boundsB.y + boundsB.h,
      },
    ];
  } else {
    // For vertical measurements, extend horizontally from the TO point
    // to the left and right edges of shape B
    extensionLines = [
      // Extension to left edge of shape B
      {
        x1: to.x,
        y1: to.y,
        x2: boundsB.x,
        y2: to.y,
      },
      // Extension to right edge of shape B
      {
        x1: to.x,
        y1: to.y,
        x2: boundsB.x + boundsB.w,
        y2: to.y,
      },
    ];
  }

  return (
    <g>
      {/* Main measurement line */}
      {shouldBreakLine ? (
        <>
          <line
            x1={from.x}
            y1={from.y}
            x2={midX - dx}
            y2={midY - dy}
            stroke="#ff6b00"
            strokeWidth={strokeWidth}
            strokeDasharray="4 2"
          />

          <line
            x1={midX + dx}
            y1={midY + dy}
            x2={to.x}
            y2={to.y}
            stroke="#ff6b00"
            strokeWidth={strokeWidth}
            strokeDasharray="4 2"
          />
        </>
      ) : (
        <line
          x1={from.x}
          y1={from.y}
          x2={to.x}
          y2={to.y}
          stroke="#ff6b00"
          strokeWidth={strokeWidth}
          strokeDasharray="4 2"
        />
      )}

      {/* Extension lines at END point - extending to shape B edges */}
      {extensionLines.map((line, index) => (
        <line
          key={index}
          x1={line.x1}
          y1={line.y1}
          x2={line.x2}
          y2={line.y2}
          stroke="#ff6b00"
          strokeWidth={strokeWidth * 0.5}
          opacity={1}
          strokeDasharray="2 2"
        />
      ))}

      {/* Text background */}
      <rect
        x={midX - textWidth / 2}
        y={midY - textHeight / 2}
        width={textWidth}
        height={textHeight}
        fill="white"
        stroke="#ff6b00"
        strokeWidth={strokeWidth}
        rx={borderRadius}
        ry={borderRadius}
      />

      {/* Distance text */}
      <text
        x={midX}
        y={midY}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="#ff6b00"
        fontSize={fontSize}
        fontWeight="600"
        fontFamily="sans-serif"
      >
        {distance}
      </text>
    </g>
  );
};
